import { useState, type ReactNode } from 'react';
import { saveCredentials, CredentialValidationError } from '../../api/client';
import RevenueCatHelp from './RevenueCatHelp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { X, Plus, Loader2 } from 'lucide-react';

interface FieldConfig {
  key: string;
  label: string;
  placeholder: string;
  multi?: boolean;
  optional?: boolean;
}

interface ProviderConfig {
  provider: string;
  fields: FieldConfig[];
  /** Optional block rendered under the fields — how to obtain the credentials. */
  help?: ReactNode;
}

const PROVIDER_FIELDS: Record<string, ProviderConfig> = {
  'revenuecat': {
    provider: 'revenuecat',
    // Labels, placeholders and the optional project id match the back office's
    // own Connect RevenueCat dialog (VIBE-2147). `optional` renders the
    // "(optional)" suffix below, so the label itself must not repeat it.
    fields: [
      { key: 'api_key', label: 'API key (secret)', placeholder: 'sk_...' },
      {
        key: 'project_id',
        label: 'RevenueCat project ID',
        placeholder: 'proj1ab2c3d4',
        optional: true,
      },
    ],
    help: <RevenueCatHelp />,
  },
  'meta-ads': {
    provider: 'meta',
    fields: [
      { key: 'access_token', label: 'Access Token', placeholder: 'EAA...' },
      { key: 'ad_account_ids', label: 'Ad Account IDs', placeholder: 'act_...', multi: true },
    ],
  },
  'tiktok-ads': {
    provider: 'tiktok',
    fields: [
      { key: 'access_token', label: 'Access Token', placeholder: '' },
      { key: 'advertiser_ids', label: 'Advertiser IDs', placeholder: '7572228664...', multi: true },
    ],
  },
  'google-ads': {
    provider: 'google_ads',
    fields: [
      { key: 'customer_ids', label: 'Customer IDs', placeholder: '123-456-7890', multi: true },
      { key: 'refresh_token', label: 'Refresh Token', placeholder: '1//0...' },
    ],
  },
  'bigquery': {
    provider: 'bigquery',
    fields: [
      { key: 'project', label: 'Project ID', placeholder: 'my-project' },
      { key: 'dataset', label: 'Dataset', placeholder: 'my_dataset' },
      { key: 'service_account', label: 'Service Account JSON', placeholder: '{"type":"service_account",...}' },
    ],
  },
  // AppsFlyer is intentionally absent — it has a dedicated stepped flow with
  // an app picker (AppsFlyerCredentialModal), branched in PlatformCard.
};

interface CredentialModalProps {
  platformId: string;
  platformName: string;
  onClose: () => void;
  onConnected: () => void;
}

export default function CredentialModal({ platformId, platformName, onClose, onConnected }: CredentialModalProps) {
  const config = PROVIDER_FIELDS[platformId];
  const [values, setValues] = useState<Record<string, string>>({});
  const [multiValues, setMultiValues] = useState<Record<string, string[]>>({});
  const [multiInput, setMultiInput] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Secondary line below the bold error — used to surface the backend's
  // detailed validator message (e.g. "project_id '…' not found. Available: […]")
  // while the primary line stays the short user-facing "Incorrect credentials".
  const [errorHint, setErrorHint] = useState('');

  const addMultiValue = (key: string) => {
    const val = (multiInput[key] || '').trim();
    if (!val) return;
    const current = multiValues[key] || [];
    if (current.includes(val)) return;
    setMultiValues({ ...multiValues, [key]: [...current, val] });
    setMultiInput({ ...multiInput, [key]: '' });
  };

  const removeMultiValue = (key: string, val: string) => {
    const current = multiValues[key] || [];
    setMultiValues({ ...multiValues, [key]: current.filter((v) => v !== val) });
  };

  const isComplete = () => {
    if (!config) return false;
    const requiredFilled = config.fields.every((f) => {
      if (f.optional) return true;
      if (f.multi) return (multiValues[f.key] || []).length > 0;
      return values[f.key]?.trim();
    });
    return requiredFilled;
  };

  const handleSave = async () => {
    if (!config) return;

    const missing = config.fields.filter((f) => {
      if (f.optional) return false;
      if (f.multi) return (multiValues[f.key] || []).length === 0;
      return !values[f.key]?.trim();
    });
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }

    // Build credentials payload — multi fields as arrays, rest as strings
    // Skip empty optional fields
    const credentials: Record<string, string | string[]> = {};
    for (const field of config.fields) {
      if (field.multi) {
        credentials[field.key] = multiValues[field.key] || [];
      } else {
        const val = values[field.key]?.trim();
        if (field.optional && !val) continue;
        credentials[field.key] = values[field.key];
      }
    }

    setSaving(true);
    setError('');
    setErrorHint('');
    try {
      await saveCredentials(config.provider, credentials as Record<string, string>);
      onConnected();
      onClose();
    } catch (err) {
      if (err instanceof CredentialValidationError) {
        // Backend probed the provider and got a definite "no" (HTTP 422).
        // Show a short, generic primary line so it reads the same across
        // providers (RevenueCat, future Meta/TikTok validators, …), and
        // expose the backend's detailed message as a secondary hint so
        // the user can actually fix the typo (e.g. "Available: [proj…]").
        setError('Incorrect credentials');
        setErrorHint(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save credentials');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {platformName}</DialogTitle>
          <DialogDescription>
            {config
              ? 'Enter your API credentials to connect this data source.'
              : 'Direct API connection is not yet available for this platform.'}
          </DialogDescription>
        </DialogHeader>

        {config ? (
          <div className="space-y-4">
            {config.fields.map((field) =>
              field.multi ? (
                <div key={field.key} className="space-y-1.5">
                  <Label>{field.label}</Label>
                  {(multiValues[field.key] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(multiValues[field.key] || []).map((val) => (
                        <Badge key={val} variant="secondary" className="gap-1 pr-1 font-mono text-xs">
                          {val}
                          <button
                            type="button"
                            onClick={() => removeMultiValue(field.key, val)}
                            className="ml-0.5 rounded-sm hover:bg-muted-foreground/20"
                          >
                            <X className="size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      placeholder={field.placeholder}
                      value={multiInput[field.key] || ''}
                      onChange={(e) => setMultiInput({ ...multiInput, [field.key]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addMultiValue(field.key);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => addMultiValue(field.key)}
                      disabled={!(multiInput[field.key] || '').trim()}
                    >
                      <Plus className="size-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={field.key}>
                    {field.label}
                    {field.optional && <span className="ml-1 text-muted-foreground font-normal">(optional)</span>}
                  </Label>
                  <Input
                    id={field.key}
                    type={field.key.includes('token') || field.key.includes('key') || field.key.includes('secret') ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={values[field.key] || ''}
                    onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                  />
                </div>
              ),
            )}

            {error && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">{error}</p>
                {errorHint && (
                  <p className="text-xs text-muted-foreground">{errorHint}</p>
                )}
              </div>
            )}

            {/* Below the error on purpose: the help block is long enough to
                push a submit error out of view on a short viewport. */}
            {config.help}

            <div className="flex items-center justify-end gap-2">
              <Button size="lg" className="font-semibold tracking-tight" onClick={handleSave} disabled={saving || !isComplete()}>
                {saving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect'
                )}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
